import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Container, Row, Col, Spinner } from 'react-bootstrap';
import PageTitle from '../../common/PageTitle';
import { useUser } from '../../../context/UserContext';
import { useToast } from '../../../context/ToastContext';
import braUfCities from '../../../assets/json/braUfCities.json';
import { apiLoadUser, apiUpdateUser, apiUpdatePassword, apiUpdateLgpd } from '../../../api/api';
import { uSubscriptionName, uSubscriptionExpiration, uSearchesRemaining } from '../../../utils';
import { useNavigate } from 'react-router-dom';

const MyProfile = () => {
  const { addToast } = useToast();
  const { user, updateUser } = useUser();
  const navigate = useNavigate();
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingLgpd, setLoadingLgpd] = useState(false);
  const [loadingPass, setLoadingPass] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [dbUser, setDbUser] = useState(null);

  const [userPlanDeadline, setUserPlanDeadline] = useState('-');
  const [userPlanName, setUserPlanName] = useState('-');
  const [userSearchesRemaining, setUserSearchesRemaining] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const dbUser = await apiLoadUser(user.id);
      setSelectedState(dbUser.state);
      setSelectedCity(dbUser.city);
      setDbUser(dbUser);
    }

    if (user) {
      setUserPlanName(uSubscriptionName(user.subscription));
      setUserPlanDeadline(uSubscriptionExpiration(user.releasedDeadline));
      setUserSearchesRemaining(uSearchesRemaining(user.searches));

      fetchData();
    }
  }, [user]);

  useEffect(() => {
    if (selectedState) {
      const state = braUfCities.find(uf => uf.sigla === selectedState);
      setCities(state ? state.cidades : []);
    } else {
      setCities([]);
    }
  }, [selectedState]);

  const handleSubmitUser = async (e) => {
    e.preventDefault();

    const formName = e.target.elements.name.value;
    const formMobile = e.target.elements.mobile.value;
    const formState = e.target.elements.state.value;
    const formCity = e.target.elements.city.value;

    const updatedUser = {
      ...user,
      name: formName,
      mobile: formMobile,
      state: formState,
      city: formCity,
    };
    try {
      const response = await apiUpdateUser(user.id, updatedUser);
      if (response.ok) {
        updateUser(updatedUser);
        addToast('Dados atualizados com sucesso');
      } else {
        addToast('Erro ao atualizar dados');
      }
    } catch (error) {
      addToast('Erro ao atualizar dados');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSubmitPass = async (e) => {
    e.preventDefault();

    const pass1 = e.target.elements.pass1.value;
    const pass2 = e.target.elements.pass2.value;

    if (pass1 !== pass2) {
      addToast('Senhas não conferem');
      return;
    }
    if (pass1.length < 6) {
      addToast('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      setLoadingPass(true);
      const response = await apiUpdatePassword(user.id, pass1);
      if (response.ok) {
        addToast('Senha alterada com sucesso');
      } else {
        addToast('Erro ao alterar senha');
      }
    } catch (error) {
      addToast('Erro ao alterar senha');
    } finally {
      setLoadingPass(false);
      e.target.elements.pass1.value = '';
      e.target.elements.pass2.value = '';
    }
  };

  const handleSubmitLgpd = async (e) => {
    e.preventDefault();
    setLoadingLgpd(true);
    const response = await apiUpdateLgpd(user.id, user.lgpd);
    const updatedUserDetails = {
      ...user,
      lgpd: response,
    };
    updateUser(updatedUserDetails);
    addToast('Termos da LGPD atualizados');
    setLoadingLgpd(false);
  };

  const handleContract = () => {
    navigate('/main/plans');
  };

  return (
    <>
      <Container>
        <Row>
          <Col>
            <PageTitle text='MEU PERFIL' />
          </Col>
        </Row>
      </Container>
      <Container>
        <Row>
          {/* Formulário principal */}
          <Col md={8}>
            <Card>
              <Card.Header className="bg-secondary text-white">
                MEUS DADOS
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmitUser}>
                  {/* E-mail */}
                  <Form.Group className="mb-3">
                    <Form.Label>E-mail:</Form.Label>
                    <Form.Control
                      size='sm'
                      type='email'
                      name='email'
                      defaultValue={dbUser && dbUser.email}
                      readOnly
                      disabled
                    />
                  </Form.Group>

                  {/* Nome */}
                  <Form.Group className="mb-3">
                    <Form.Label>Nome:</Form.Label>
                    <Form.Control
                      size='sm'
                      type="text"
                      name='name'
                      defaultValue={dbUser && dbUser.name}
                      required
                    />
                  </Form.Group>

                  {/* Telefone */}
                  <Form.Group className="mb-3">
                    <Form.Label>Telefone:</Form.Label>
                    <Form.Control
                      size='sm'
                      type='text'
                      name='mobile'
                      defaultValue={dbUser && dbUser.mobile}
                    />
                  </Form.Group>

                  {/* Estado */}
                  <Form.Group className="mb-3">
                    <Form.Label>Estado:</Form.Label>
                    <Form.Select
                      size='sm'
                      name='state'
                      value={selectedState ? selectedState : ''}
                      onChange={(e) => setSelectedState(e.target.value)}
                    >
                      {braUfCities.map((uf, index) => (
                        <option key={uf.sigla} value={uf.sigla}>{uf.sigla + '/' + uf.nome}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {/* Cidade */}
                  <Form.Group className="mb-3">
                    <Form.Label>Cidade:</Form.Label>
                    <Form.Select
                      size='sm'
                      name='city'
                      disabled={!selectedState}
                      value={selectedCity ? selectedCity : ''}
                      onChange={(e) => setSelectedCity(e.target.value)}
                    >
                      {cities.map((city, index) => (
                        <option key={index} value={city}>{city}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Button type='submit' variant="primary" size="sm" className='w-100 me-1 w-25' disabled={loadingUser}>
                    {loadingUser ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Salvar Dados'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <Card className="mt-4">
              <Card.Header className="bg-secondary text-white">
                ALTERAR SENHA
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmitPass}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nova senha:</Form.Label>
                    <Form.Control type="password" name="pass1"
                      size='sm' />
                  </Form.Group>

                  {/* Repetir a senha */}
                  <Form.Group className="mb-3">
                    <Form.Label>Repetir a senha:</Form.Label>
                    <Form.Control type="password" name="pass2"
                      size='sm' />
                  </Form.Group>

                  {/* Botão salvar */}
                  <Button type="submit" variant="primary" size="sm" className='w-100 me-1 w-25' disabled={loadingPass}>
                    {loadingPass ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Salvar Senha'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Informações LGPD */}
          <Col md={4}>
            <Card>
              <Card.Header className="bg-secondary text-white">
                ASSINATURA
              </Card.Header>
              <Card.Body >
                <Row>
                  <Col xs={4} className="flex-item">Nome:</Col>
                  <Col xs={8} className="flex-item">{user.name}</Col>
                </Row>
                <Row>
                  <Col xs={4} className="flex-item">E-mail:</Col>
                  <Col xs={8} className="flex-item">{user.email}</Col>
                </Row>
                <Row>
                  <Col xs={4} className="flex-item">Plano:</Col>
                  <Col xs={8} className="flex-item">{userPlanName}</Col>
                </Row>
                <Row>
                  <Col xs={4} className="flex-item">Vencimento:</Col>
                  <Col xs={8} className="flex-item">{userPlanDeadline}</Col>
                </Row>
                {userPlanDeadline && (userPlanDeadline === '-' || userPlanDeadline === 'Grátis') &&
                  <>
                    <Row>
                      <Col xs={4} className="flex-item">Pesquisas:</Col>
                      <Col xs={8} className="flex-item">{userSearchesRemaining} de 5</Col>
                    </Row>
                    <Row className="justify-content-center mt-3">
                      <Col md="auto"><Button variant="success" onClick={handleContract} >CONTRATAR</Button></Col>
                    </Row>
                  </>
                }
              </Card.Body>
            </Card>

            <Card className="mt-4">
              <Card.Header className="bg-secondary text-white">
                LGPD
              </Card.Header>
              <Card.Body className='text-center'>
                {user.lgpd ?
                  <div><p>
                    Você aceitou nossos termos de proteção do uso de dados e tem total liberdade para revogar este aceite utilizando o botão abaixo.
                  </p>
                    <p>
                      Revogando o acesso você precisará aceitar novamente os termos.
                    </p>
                    <p>
                      Em momento algum seus dados serão compartilhados. Somente serão utilizados para prover o melhor suporte possível e gerar estatísticas de uso para a constante atualização do sistema.
                    </p></div>
                  :
                  <p>
                    Para continuar utilizando nossos serviços, aceite <a href="../lgpd" target="_blank">nossos termos</a> de proteção do uso de dados.
                  </p>
                }
                <Button onClick={handleSubmitLgpd} variant={user.lgpd ? 'danger' : 'success'} size="sm" className='w-100 me-1 w-25' disabled={loadingLgpd}>
                  {loadingLgpd ?
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                    :
                    user.lgpd ? 'CANCELAR' : 'ACEITAR'
                  }
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default MyProfile;